/**
 * Mic-consent first-use disclosure (CL-135).
 *
 * Before the FIRST time voice listening starts, a one-time notice explains
 * that audio is processed by the browser's built-in speech service (Web
 * Speech API) — EarthVoice never receives or stores audio — and how the
 * resulting query text is retained (max 30 days, truncated to 100 chars,
 * per CL-132). Acknowledgement is persisted to localStorage under
 * 'ev:mic-notice-ack' so the modal appears once per browser.
 *
 * needsMicConsent / acknowledgeMicNotice take an injectable storage so they
 * can be unit-tested without a DOM (see tests/mic-consent.test.js).
 */
import { createLogger } from '../utils/logger.js'

const log = createLogger('mic-consent')

export const MIC_NOTICE_KEY = 'ev:mic-notice-ack'

function defaultStorage() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null
  } catch {
    // localStorage access can throw in sandboxed contexts
    return null
  }
}

/**
 * True when the first-use mic notice has not been acknowledged yet.
 * When storage is unavailable (sandboxed iframe, privacy mode) we return
 * false rather than dead-ending voice input behind a modal that could
 * never persist its acknowledgement.
 */
export function needsMicConsent(storage = defaultStorage()) {
  if (!storage) return false
  try {
    return !storage.getItem(MIC_NOTICE_KEY)
  } catch {
    return false
  }
}

/** Persist acknowledgement (ISO timestamp, for auditability). */
export function acknowledgeMicNotice(storage = defaultStorage()) {
  try {
    storage?.setItem(MIC_NOTICE_KEY, new Date().toISOString())
  } catch {
    log.warn('could not persist mic-notice acknowledgement; notice may reappear')
  }
}

/**
 * Show the one-time disclosure modal. All text is static strings assigned
 * via textContent (XSS TIER 1).
 *
 * @param {object} opts
 * @param {Function} [opts.onAccept]  called after the flag is set — caller
 *   should start listening so the user's original action completes
 * @param {Function} [opts.onDismiss] called on "Not now" / Escape / backdrop
 */
export function showMicConsentModal({ onAccept, onDismiss } = {}) {
  if (document.querySelector('.mic-consent-overlay')) return

  const overlay = document.createElement('div')
  overlay.className = 'mic-consent-overlay'

  const modal = document.createElement('div')
  modal.className = 'mic-consent-modal'
  modal.setAttribute('role', 'dialog')
  modal.setAttribute('aria-modal', 'true')
  modal.setAttribute('aria-labelledby', 'mic-consent-title')

  const title = document.createElement('h2')
  title.className = 'mic-consent-title'
  title.id = 'mic-consent-title'
  title.textContent = 'Before you use voice'

  const body = document.createElement('p')
  body.className = 'mic-consent-body'
  body.textContent =
    "When you speak, your browser's built-in speech service (for example, Google's service in Chrome) turns your voice into text. EarthVoice never records or stores audio. The text of your query is kept for at most 30 days and truncated to 100 characters."

  // TODO(counsel): once the privacy policy is approved and published, turn
  // this into a real link — e.g. <a href="/privacy.html">Privacy policy</a>.
  // Do NOT link while docs/legal/PRIVACY-POLICY.md is still a draft.
  const note = document.createElement('p')
  note.className = 'mic-consent-note'
  note.textContent = 'See the EarthVoice privacy policy for details.'

  const actions = document.createElement('div')
  actions.className = 'mic-consent-actions'

  const dismissBtn = document.createElement('button')
  dismissBtn.type = 'button'
  dismissBtn.className = 'mic-consent-dismiss'
  dismissBtn.textContent = 'Not now'

  const acceptBtn = document.createElement('button')
  acceptBtn.type = 'button'
  acceptBtn.className = 'mic-consent-accept'
  acceptBtn.textContent = 'Got it — enable voice'

  actions.appendChild(dismissBtn)
  actions.appendChild(acceptBtn)
  modal.appendChild(title)
  modal.appendChild(body)
  modal.appendChild(note)
  modal.appendChild(actions)
  overlay.appendChild(modal)

  function close(accepted) {
    document.removeEventListener('keydown', onKeydown, true)
    overlay.remove()
    if (accepted) {
      acknowledgeMicNotice()
      log.info('mic notice acknowledged')
      onAccept?.()
    } else {
      onDismiss?.()
    }
  }

  // Capture-phase so Escape closes the modal instead of triggering the
  // app-wide Escape=reset binding in ui/overlay.js, and so Space activates
  // the focused button instead of toggling voice globally.
  function onKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      close(false)
    } else if (e.code === 'Space' && (e.target === acceptBtn || e.target === dismissBtn)) {
      e.stopPropagation()
    }
  }

  acceptBtn.addEventListener('click', () => close(true))
  dismissBtn.addEventListener('click', () => close(false))
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close(false)
  })
  document.addEventListener('keydown', onKeydown, true)

  document.body.appendChild(overlay)
  acceptBtn.focus()
}
